import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import {
    IBackendErrorContentDetails,
    IBackendErrorUserEmailInconsistentData,
} from '@fi-shared/fi-backend/fi-backend.service';
import { AppTextService } from '@fi-shared/app-text';
import { IContactModel } from '@fi-shared/models/common';
import { isDefined } from '@fi-shared/utils';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { FiCustomValidatorPatterns } from '@fi-shared/fi-custom-validator-patterns/fi-custom-validator-patterns';
import { IFiUniqueContactData } from '@fi-shared/fi-directives/fi-custom-validators/fi-unique-contact-async-validator.directive';
import { FiValidatingFormDirective } from '@fi-shared/fi-directives/fi-validating-form/fi-validating-form.directive';

@Component({
    selector: 'fi-user-email-inconsistent-dialog',
    templateUrl: './fi-user-email-inconsistent-dialog.component.html',
    styleUrls: ['./fi-user-email-inconsistent-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-user-email-inconsistent-dialog' },
    standalone: false,
})
export class FiUserEmailInconsistentDialogComponent
    extends FiDialogsContentBase<IBackendErrorUserEmailInconsistentData, { selectedEmail: string }>
    implements OnInit, AfterViewInit
{
    private appTextService: AppTextService;
    private cdr: ChangeDetectorRef;
    selectedEmail: string;

    @ViewChild(FiValidatingFormDirective) emailForm: FiValidatingFormDirective;

    get availableEmails() {
        return this.data.AvailableEmails;
    }
    get unavailableEmails() {
        return this.data.UnavailableEmails;
    }

    ngOnInit(): void {
        this.appTextService = this.injector.get(AppTextService);
        this.cdr = this.injector.get(ChangeDetectorRef);
    }

    ngAfterViewInit(): void {
        this.emailForm.errors$.subscribe((errors) => {
            this.cdr.markForCheck();
        });
    }

    isNoAvailableEmail() {
        return this.availableEmails.length === 0;
    }

    isSingleAvailableEmail() {
        return this.availableEmails.length === 1;
    }

    isMultipleAvailableEmails() {
        return this.availableEmails.length > 1;
    }

    getSignInWithEmailText() {
        let text = this.appTextService.get('login.signin_update_text2');
        if (this.isSingleAvailableEmail() && !this.data.LoginIsEmail) {
            text += `: ${this.getEmailHtml(this.availableEmails[0].ContactValue)}`;
        }
        return text + '.';
    }

    getNoAvailableEmailsText() {
        let text: string;
        const unavailableEmailsCount = this.unavailableEmails.length;
        if (unavailableEmailsCount === 0) {
            text = this.appTextService.get('login.no_available_emails_text');
        } else if (unavailableEmailsCount === 1) {
            if (this.data.LoginIsEmail) {
                // https://chainformation.atlassian.net/wiki/spaces/FI/pages/2223768394/Email+their+username+is+associated+with+other+accounts+-+No+unique+emails+in+profile
                // Several accounts are associated with your sign in email <user's sign in email>. Please enter a new email address to use when signing in:
                text = this.appTextService
                    .get('login.single_unavailable_login_email_text_enter_new')
                    .replace('{email}', this.getEmailHtml(this.data.EnteredLogin));
            } else {
                // https://chainformation.atlassian.net/wiki/spaces/FI/pages/2220228609/Email+found+on+other+accounts+-+Signs+in+with+username+not+email
                // The email address in your profile <user’s email address> is associated with several accounts. Please enter a new email address to use when signing in:
                text = this.appTextService
                    .get('login.single_unavailable_email_text')
                    .replace('{email}', this.getEmailHtml(this.unavailableEmails[0].ContactValue));
            }
        } else if (unavailableEmailsCount > 1) {
            // https://chainformation.atlassian.net/wiki/spaces/FI/pages/2220228609/Email+found+on+other+accounts+-+Signs+in+with+username+not+email
            // The email addresses in your profile are associated with several accounts.
            text = this.appTextService.get('login.multi_unavailable_emails_text');
        }
        return text;
    }

    getSingleAvailableEmailText() {
        let text: string;
        // https://chainformation.atlassian.net/wiki/spaces/FI/pages/2216657248/Signs+in+with+username+-+not+an+email
        // by default empty text
        if (this.data.LoginIsEmail && this.unavailableEmails.length > 0) {
            // https://chainformation.atlassian.net/wiki/spaces/FI/pages/2223768827/Email+their+username+is+associated+with+other+accounts+-+1+unique+email+in+profile
            // Several accounts are associated with your sign in email <user's sign in email>. Please confirm that you will use the email address from your profile's contact list to sign in with from now on.
            text = this.appTextService
                .get('login.single_unavailable_login_email_text_confirm_existing')
                .replace('{email}', this.getEmailHtml(this.data.EnteredLogin));
        }
        return text;
    }
    getSignInWithEmailConfirmText() {
        let text: string;
        if (this.data.LoginIsEmail && this.unavailableEmails.length > 0) {
            // https://chainformation.atlassian.net/wiki/spaces/FI/pages/2223768827/Email+their+username+is+associated+with+other+accounts+-+1+unique+email+in+profile
            // I confirm that I will use <user’s email> when signing in from now on.
            text = this.appTextService
                .get('login.signin_with_email_confirm_text_will_use')
                .replace('{email}', this.getEmailHtml(this.availableEmails[0].ContactValue));
        } else {
            // https://chainformation.atlassian.net/wiki/spaces/FI/pages/2216657248/Signs+in+with+username+-+not+an+email
            // I understand that from now on I can on only sign in with my email address.
            text = this.appTextService.get('login.signin_with_email_confirm_text');
        }
        return text;
    }

    getMultipleAvailableEmailTexts() {
        let text: string;
        if (this.data.LoginIsEmail && this.unavailableEmails.length > 0) {
            // https://chainformation.atlassian.net/wiki/spaces/FI/pages/2223768002/Email+their+username+is+associated+with+other+accounts+-+2+unique+emails+in+profile
            // Several accounts are associated with your sign in email <user's sign in email>. Please select which email address from your profile's contact list you want to use for signing in.
            text = this.appTextService
                .get('login.multiple_available_emails_text_several_accounts')
                .replace('{email}', this.getEmailHtml(this.data.EnteredLogin));
        } else {
            // https://chainformation.atlassian.net/wiki/spaces/FI/pages/2216853505/2+emails+in+profil+-+signs+in+with+username+not+email
            // You have more than one email address in your profile. Please select which email address you want to use when signing in:
            text = this.appTextService.get('login.multiple_available_emails_text');
        }
        return text;
    }

    private getEmailHtml(login: string) {
        return `<span class="BrandedColor">${login}</span>`;
    }

    getResult() {
        return {
            selectedEmail: this.selectedEmail,
        };
    }

    isYesButtonDisabled(): boolean {
        return !!this.emailForm.errors || this.emailForm.pending;
    }

    getEmailSelectText(emailContact: IContactModel) {
        return this.appTextService.get('login.use_email_for_signin_text').replace('{email}', emailContact.ContactValue);
    }

    // confirming when only one email available
    signinWithEmailConfirmChanged($event: MatCheckboxChange) {
        if ($event.checked) {
            this.selectedEmail = this.availableEmails[0].ContactValue;
        } else {
            this.selectedEmail = undefined;
        }
        this.selectedEmailChanged();
    }

    getDataForUniqueEmailCheck(): IFiUniqueContactData {
        return {
            userId: this.data.UserId,
            doCheck: true,
        };
    }

    selectedEmailChanged() {
        this.emailForm.updateValidity();
    }

    protected readonly FiCustomValidatorPatterns = FiCustomValidatorPatterns;
}
